// Typed calls for Purchasing (Sprint 4): the routes of packages/contracts/src/openapi/inventory.ts §5.15 — the supplier master (the
// list and every supplier picker, a supplier's read, add and edit, deactivation and reactivation, its deletion as recorded in error,
// and its lab lines) and the goods receipt without a purchase order (UC-PURCHASING-005, -008: posting one, the list, one as posted).
// Response schemas are the contract's own.
import {
  GoodsReceiptListItemSchema, GoodsReceiptViewSchema, GoodsReceiptWriteResultSchema, LabLineViewSchema, SupplierListItemSchema, SupplierStatusChangeResultSchema,
  SupplierViewSchema, SupplierVoidResultSchema,
  type GoodsReceiptInput, type LabLineKind, type SupplierStatusFilter, type SupplierType,
} from "@visionx/contracts";
import { request } from "./client";
import { PageSchema, type Cursor, type Grant } from "./endpoints";

export interface SupplierFilters { q?: string | undefined; type?: SupplierType | undefined; status?: SupplierStatusFilter | undefined }
/** A supplier's body as the form sends it; the contract's schema and the server's rules judge it (UC-PURCHASING-001, -002). */
export type SupplierBody = Record<string, unknown>;

export interface LabLineFilters { q?: string | undefined; kind?: LabLineKind | undefined; design?: string | undefined; index?: string | undefined; asAt?: string | undefined }

export const suppliers = {
  /** UC-PURCHASING-003: by name, 50 a page; `status` Active unless asked; each row with the acting branch's account number. */
  list: (f: SupplierFilters, c: Cursor = {}) => request("/suppliers", { query: { q: f.q, type: f.type, status: f.status ?? "Active", limit: c.limit ?? 50, cursor: c.cursor }, schema: PageSchema(SupplierListItemSchema) }),
  get: (id: string) => request(`/suppliers/${id}`, { schema: SupplierViewSchema }),
  create: (body: SupplierBody, idempotencyKey: string) => request("/suppliers", { method: "POST", body, idempotencyKey, schema: SupplierViewSchema }),
  patch: (id: string, body: SupplierBody, ifMatch: string) => request(`/suppliers/${id}`, { method: "PATCH", body, ifMatch, schema: SupplierViewSchema }),
  /** UC-PURCHASING-004: naturally idempotent, an optional reason; a repeat answers `changed: false`. */
  deactivate: (id: string, reason: string | null) => request(`/suppliers/${id}/deactivate`, { method: "POST", body: reason ? { reason } : {}, schema: SupplierStatusChangeResultSchema }),
  activate: (id: string) => request(`/suppliers/${id}/activate`, { method: "POST", body: {}, schema: SupplierStatusChangeResultSchema }),
  /** UC-PURCHASING-007: versioned and keyed, a Control PIN (`Delete`); refused 409 `has_history` naming every kind before any PIN. */
  void: (id: string, reason: string, ifMatch: string, g: Grant) =>
    request(`/suppliers/${id}/void`, { method: "POST", body: { reason }, ifMatch, stepUpToken: g.token, idempotencyKey: g.idempotencyKey, schema: SupplierVoidResultSchema }),
  /** UC-PURCHASING-006: the lines in force on `asAt` (today unless given) at the acting branch. */
  labLines: (id: string, f: LabLineFilters, c: Cursor = {}) => request(`/suppliers/${id}/lab-lines`, {
    query: { q: f.q, kind: f.kind, design: f.design, index: f.index, asAt: f.asAt, limit: c.limit ?? 50, cursor: c.cursor }, schema: PageSchema(LabLineViewSchema),
  }),
};

/** UC-PURCHASING-008 step 3 (BR-PURCHASING-021): the list's filters as the toolbar keeps them in the address. */
export interface ReceiptFilters {
  /** The acting branch unless named; `all` for an Owner or Admin. */
  branchId?: string | undefined;
  supplierId?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  withoutOrder?: boolean | undefined;
  deliveryNoteNumber?: string | undefined;
}

export const goodsReceipts = {
  /** UC-PURCHASING-008: newest first — received date, then number — 50 a page; `purchasing.po.read`. */
  list: (f: ReceiptFilters, c: Cursor = {}) => request("/goods-receipts", {
    query: {
      branchId: f.branchId, supplierId: f.supplierId, from: f.from, to: f.to, withoutOrder: f.withoutOrder ? "true" : undefined, deliveryNoteNumber: f.deliveryNoteNumber,
      limit: c.limit ?? 50, cursor: c.cursor,
    },
    schema: PageSchema(GoodsReceiptListItemSchema),
  }),
  /** UC-PURCHASING-008 step 4: one receipt exactly as posted; another branch's the user is not assigned to is `404`. */
  get: (id: string) => request(`/goods-receipts/${id}`, { schema: GoodsReceiptViewSchema }),
  /**
   * UC-PURCHASING-005: at the acting branch, keyed — the same receipt sent again under its key posts once (A12) — with the Control
   * PIN's token when a kept price is below its floor (`PriceOverride`, A6).
   */
  post: (body: GoodsReceiptInput, g: Grant) => request("/goods-receipts", { method: "POST", body, idempotencyKey: g.idempotencyKey, stepUpToken: g.token, schema: GoodsReceiptWriteResultSchema }),
};
